import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Home, RotateCcw, Trophy, Sparkles, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSfx } from '../../store/sfxStore';

interface ModuleSuccessProps {
    title: string;
    onRetry?: () => void;
    nextModulePath?: string;
    badgeId?: string;
    children?: React.ReactNode;
}

export const ModuleSuccess = ({ title, onRetry, nextModulePath, badgeId, children }: ModuleSuccessProps) => {
    const navigate = useNavigate();
    const { play } = useSfx();
    const isCertificate = nextModulePath === '/diploma';

    useEffect(() => {
        play('success');
    }, []);

    // Confetti particles
    const [particles] = useState(() => Array.from({ length: 40 }).map((_, i) => ({
        id: i,
        x: Math.random() * 100, // % position
        delay: Math.random() * 2,
        color: ['#ef4444', '#22c55e', '#3b82f6', '#eab308', '#a855f7'][Math.floor(Math.random() * 5)]
    })));

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        >
            {/* Falling Confetti */}
            {particles.map((p) => (
                <motion.div
                    key={p.id}
                    className="absolute w-2 h-2 rounded-full pointer-events-none"
                    style={{
                        left: `${p.x}%`,
                        backgroundColor: p.color,
                        top: '-10px'
                    }}
                    animate={{
                        top: ['0vh', '100vh'],
                        rotate: [0, 360]
                    }}
                    transition={{
                        duration: 3,
                        delay: p.delay,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                />
            ))}

            <motion.div
                initial={{ scale: 0.8, y: 50 }}
                animate={{ scale: 1, y: 0 }}
                className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl text-center relative overflow-hidden"
            >
                {/* Header Gradient */}
                <div className="absolute top-0 left-0 w-full h-3 bg-gradient-to-r from-green-400 via-yellow-400 to-blue-500" />

                {/* Animated Trophy */}
                <div className="mb-6 inline-block relative">
                    <motion.div
                        animate={{ rotate: [0, 10, -10, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <Trophy size={80} className="text-yellow-500 fill-yellow-100 drop-shadow-lg" />
                    </motion.div>
                    <motion.div
                        animate={{ scale: [1, 1.2, 1], rotate: [0, 15, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="absolute -top-2 -right-4"
                    >
                        <Sparkles size={32} className="text-yellow-400 fill-yellow-200" />
                    </motion.div>
                </div>

                <h2 className="text-4xl font-black text-slate-800 mb-2 font-comic tracking-tight">
                    {isCertificate ? 'Course Complete!' : 'Great Job!'}
                </h2>

                {badgeId && (
                    <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        className="my-4 flex flex-col items-center"
                    >
                        <span className="text-xs font-bold text-yellow-600 uppercase tracking-widest mb-2 bg-yellow-100 px-3 py-1 rounded-full">New Badge Unlocked</span>
                        <img src={`/assets/badges/${badgeId}.png`} className="w-32 h-32 drop-shadow-2xl hover:scale-110 transition-transform" />
                    </motion.div>
                )}

                <p className="text-slate-500 mb-6 font-medium text-lg">
                    Module Completed:<br />
                    <strong className="text-indigo-600 text-xl">{title}</strong>
                </p>

                {/* Custom Content (Scores, Tips) */}
                <div className="mb-8">
                    {children}
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                    {nextModulePath ? (
                        <button
                            onClick={() => { navigate(nextModulePath); play('click'); }}
                            onMouseEnter={() => play('hover')}
                            className={`w-full py-4 bg-gradient-to-r ${isCertificate ? 'from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600' : 'from-violet-500 to-fuchsia-600 hover:from-violet-600 hover:to-fuchsia-700'} text-white rounded-xl font-bold text-xl shadow-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3 animate-pulse-slow`}
                        >
                            {isCertificate ? <Award size={28} /> : <Sparkles size={24} />}
                            {isCertificate ? 'Claim Diploma' : 'Next Module →'}
                        </button>
                    ) : (
                        <button
                            onClick={() => { navigate('/'); play('click'); }}
                            onMouseEnter={() => play('hover')}
                            className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl font-bold text-xl shadow-lg shadow-green-200 transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3"
                        >
                            <Home size={24} /> Return to Map
                        </button>
                    )}

                    {onRetry && (
                        <button
                            onClick={() => { onRetry(); play('click'); }}
                            onMouseEnter={() => play('hover')}
                            className="w-full py-3 text-slate-400 hover:text-slate-600 font-bold flex items-center justify-center gap-2 transition-colors text-sm"
                        >
                            <RotateCcw size={16} /> Play Again
                        </button>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};
